import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Table, Glyphicon, DropdownButton, MenuItem, Row, Col } from 'react-bootstrap';
import Pagination from './Pagination';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans, withTranslation } from 'react-i18next';
import { Checkbox } from 'react-icheck';
import 'icheck/skins/minimal/grey.css';
import { faSpinner } from '@fortawesome/free-solid-svg-icons';

class PagedEntitiesList extends Component {
	constructor(props) {
		super(props);
		this.handlePageSelect = this.handlePageSelect.bind(this);
		this.handlePageSizeSelect = this.handlePageSizeSelect.bind(this);
		this.handleSort = this.handleSort.bind(this);
		this.handleChangeSelection = this.handleChangeSelection.bind(this);
	}

	handlePageSelect(selectedPage) {
		const { onFetchData, sortKey, sortDirection, pageSize } = this.props;
		onFetchData({
			sortKey,
			sortDirection,
			pageNumber: selectedPage,
			pageSize
		});
	}

	handlePageSizeSelect(selectedPageSize) {
		const { onFetchData, sortKey, sortDirection } = this.props;
		onFetchData({
			sortKey,
			sortDirection,
			pageNumber: 1,
			pageSize: Number(selectedPageSize)
		});
	}

	handleSort(selectedSortKey) {
		const { onFetchData, sortKey, sortDirection, pageNumber, pageSize } = this.props;
		let selectedSortDirection = 'asc';
		if (selectedSortKey === sortKey) {
			selectedSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
		}
		onFetchData({
			sortKey: selectedSortKey,
			sortDirection: selectedSortDirection,
			pageNumber,
			pageSize
		});
	}

	handleChangeSelection(rows, checked) {
		const { selectedEntities, onSelectionChange } = this.props;
		const currentSelection = selectedEntities ? [...selectedEntities] : [];

		rows.forEach(row => {
			const rowKeyValue = this.getKeyValue(row);
			const existingRowIndex = currentSelection.findIndex(x => this.getKeyValue(x) === rowKeyValue);

			if (checked && existingRowIndex < 0) {
				currentSelection.push(row);
			}

			if (!checked && existingRowIndex >= 0) {
				currentSelection.splice(existingRowIndex, 1);
			}
		});

		onSelectionChange(currentSelection);
	}

	renderHeaderCell(column, index) {
		const headerText = column.header;
		let headerClick = null, headerIcon = null, headerStyle = null;

		if (column.sortable) {
			const columnSortKey = column.sortKey || (typeof column.accessor === 'string' ? column.accessor : null);

			if (columnSortKey) {
				headerClick = () => this.handleSort(columnSortKey);

				headerStyle = { cursor: 'pointer' };

				const { sortKey, sortDirection } = this.props;
				let headerIconStyle = { float: 'right', color: '#DDDDDD' }, headerIconGlyph = 'sort';
				if (sortKey === columnSortKey) {
					headerIconStyle.color = '#707070';
					headerIconGlyph = sortDirection === 'asc' ? 'sort-by-attributes' : 'sort-by-attributes-alt';
				}
				headerIcon = <Glyphicon glyph={headerIconGlyph} style={headerIconStyle} />;
			}
		}

		return (
			<th key={index} className={column.cellClassName} onClick={!this.props.loading ? headerClick : undefined} style={headerStyle}>
				{headerText}{headerIcon}
			</th>
		);
	}

	renderDataCell(row, column, index) {
		let cellData = '', content = '';

		const { accessor, cell } = column;

		if (accessor) {
			cellData = this.getDataValue(row, accessor);
		}

		content = cellData;

		if (cell) {
			content = cell(cellData, row);
		}

		return <td key={index} className={column.cellClassName}>{content}</td>;
	}

	getDataValue(row, accessor) {
		if (typeof accessor === 'string')
			return this.getNestedValue(row, accessor);

		if (typeof accessor === 'function')
			return accessor(row);

		return accessor;
	}

	getNestedValue(obj, key) {
		return key.split(".").reduce((result, key) => result[key], obj);
	}

	getKeyValue(row) {
		const { dataKey = 'id' } = this.props;
		return this.getDataValue(row, dataKey);
	}

	render() {
		const { t, columns, data, loading, total, pages, pageNumber = 1, pageSize = 10, checkboxSelection = false, selectedEntities, dataKey = 'id', listActions } = this.props;

		const checkedKeys = new Set(selectedEntities && selectedEntities.map(x => this.getKeyValue(x)));
		if (checkboxSelection) {
			columns.unshift({
				header: <Checkbox checkboxClass='icheckbox_minimal-grey bg' increaseArea='20%'
					checked={data.every(row => checkedKeys.has(this.getKeyValue(row)))}
					onChange={(e, checked) => this.handleChangeSelection(data, checked)}
				/>,
				accessor: dataKey,
				cell: (keyValue, row) => <Checkbox checkboxClass='icheckbox_minimal-grey bg' increaseArea='20%'
					checked={checkedKeys.has(keyValue)} onChange={(e, checked) => this.handleChangeSelection([row], checked)}
				/>
			});
		}

		const headerCells = columns.map((column, index) => this.renderHeaderCell(column, index));
		const dataCells = row => columns.map((column, index) => this.renderDataCell(row, column, index));
		const pagination = total > 0 && (<div>
			{t('List_PaginationStats', { first: (pageNumber - 1) * pageSize + 1, last: Math.min(pageNumber * pageSize, total), total })}
			<Pagination style={{ float: 'right' }} totalPages={pages} currentPage={pageNumber} onChange={this.handlePageSelect} disabled={loading}/>
		</div>);
		const pageSizeSelector = <DropdownButton id='pageSizeSelector' bsSize='small' title={pageSize} onSelect={this.handlePageSizeSelect} style={{ maxWidth: 'auto' }} disabled={loading}>
			{[10, 25, 50, 100].map(x => <MenuItem key={x} eventKey={x}>{x}</MenuItem>)}
		</DropdownButton>;

		return <React.Fragment>
			<Row style={{ padding: '0 8px', fontWeight: 'bold', color: '#707070', marginBottom: '10px' }}>
				<Col sm={6}>
					{total > 0 && <React.Fragment>{t('List_PageSize_Show')} {pageSizeSelector} {t('List_PageSize_Entries')}</React.Fragment>}
					{selectedEntities && selectedEntities.length > 0 && ` | ${t('List_SelectionStats', { count: selectedEntities.length })}`}
				</Col>
				<Col sm={6}><div className='pull-right' style={{ fontWeight: 'normal' }}>{listActions}</div></Col>
			</Row>
			<Table responsive hover>
				<thead>

					<tr>{headerCells}</tr>
				</thead>
				<tbody>
					{loading ? (
						<tr><td colSpan={columns.length} style={{ textAlign: 'center' }}><FontAwesomeIcon size='2x' icon={faSpinner} spin /> {t('List_Loading')}</td></tr>
					) : (
						(data && data.length) ?
							data.map((row, index) => <tr key={index}>{dataCells(row)}</tr>) :
							<tr><td colSpan={columns.length}>{t('List_Empty')}</td></tr>
					)}
				</tbody>
				<tfoot>
					<tr>
						<th colSpan={columns.length}>
							{pagination}
						</th>
					</tr>
				</tfoot>
			</Table>
		</React.Fragment>;
	}
}

PagedEntitiesList.propTypes = {
	columns: PropTypes.arrayOf(PropTypes.shape({
		header: PropTypes.string,
		sortable: PropTypes.bool,
		accessor: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
		cell: PropTypes.func
	})),
	onFetchData: PropTypes.func,
	data: PropTypes.array,
	loading: PropTypes.bool,
	sortKey: PropTypes.string,
	sortDirection: PropTypes.string,
	total: PropTypes.number,		// total number of records
	pages: PropTypes.number,		// number of pages
	pageNumber: PropTypes.number,
	pageSize: PropTypes.number,
	enableMultipleSelection: PropTypes.bool,
	dataKey: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),	// used by multiple selection, default is 'id'
	selectedEntities: PropTypes.array,
	onSelectionChange: PropTypes.func
};

export default withTranslation()(PagedEntitiesList);